// Customer download-page helpers for labelling and grouping a senior's images by
// CAMERA. A senior session is often split across several cameras, so each frame's
// filename carries which camera shot it.
//
// Pose / student-image filenames follow `{scode}-{camera}-{token}-{NN}.ext`, e.g.
// `213-S002I-213RVXKSWM-13.png`:
//   field 0 = school code   (213)
//   field 1 = camera        (S002I)        -> camera LETTER is its last char (I)
//   field 2 = student token (213RVXKSWM)   -> stable per student, across cameras
//   field 3 = pose number   (13)

// The camera code (field 1), e.g. "S002I". '' when the url isn't the expected
// 4-field shape (e.g. a legacy student_image filename like `sync_img...png`).
export const cameraCodeOf = (image) => {
  const parts = String(image?.url || '').split('-');
  return parts.length >= 4 ? parts[1] : '';
};

// The single camera LETTER — the last char of the camera code, e.g. "I". '' when
// unknown.
export const cameraLetterOf = (image) => {
  const code = cameraCodeOf(image);
  return code ? code.slice(-1).toUpperCase() : '';
};

// The pose number as an integer — from the resource `number` (a string) when
// present, else parsed from the filename's trailing `-NN.ext`. 0 when unknown.
export const poseNumberOf = (image) => {
  const fromAttr = parseInt(image?.number, 10);
  if (!Number.isNaN(fromAttr)) return fromAttr;
  const match = String(image?.url || '').match(/-(\d+)\.\w+$/);
  return match ? parseInt(match[1], 10) : 0;
};

// A short customer-facing caption, e.g. "Camera I · Pose 13". Falls back to just
// the pose, or '' when neither is known.
export const cameraLabelOf = (image) => {
  const letter = cameraLetterOf(image);
  const pose = poseNumberOf(image);
  return [letter ? `Camera ${letter}` : '', pose ? `Pose ${pose}` : '']
    .filter(Boolean)
    .join(' · ');
};

// Comparator for `{ image }` items: group by camera LETTER, then order by pose
// number within each camera. Items with no camera letter sort last.
export const byCameraThenPose = (a, b) => {
  const la = cameraLetterOf(a?.image);
  const lb = cameraLetterOf(b?.image);
  if (la !== lb) {
    if (!la) return 1;
    if (!lb) return -1;
    return la.localeCompare(lb);
  }
  // tie-break on the full camera code so rigs that share a letter stay grouped
  const ca = cameraCodeOf(a?.image);
  const cb = cameraCodeOf(b?.image);
  if (ca !== cb) return ca.localeCompare(cb);
  return poseNumberOf(a?.image) - poseNumberOf(b?.image);
};
